interface GeoLocationResponse {
  header: string;
  userLocation: {
    location: {
      country_name: string;
      currency_code: string;
      country_code: string;
    };
    userIDLocation: {
      country_name: string;
      currency_code: string;
      country_code: string;
    };
  };
  success: number;
}

interface LocationCache {
  isInChina: boolean;
  timestamp: number;
  source: string;
}

const CACHE_KEY = 'china_location_check';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Get cached location result if available and not expired.
 */
const getCachedGeoResult = (): boolean | null => {
  try {
    if (typeof window === 'undefined') return null;

    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { isInChina, timestamp }: LocationCache = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return isInChina;
  } catch {
    return null;
  }
};

/**
 * Cache the location result.
 */
const setCachedGeoResult = (isInChina: boolean, source: string): void => {
  try {
    if (typeof window === 'undefined') return;

    const cache: LocationCache = {
      isInChina,
      timestamp: Date.now(),
      source,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Check if geolocation data indicates China.
 */
const isGeoLocationChina = (data: GeoLocationResponse): boolean => {
  if (data.header === 'CN') return true;

  const location = data.userLocation?.location;
  if (location?.country_code === 'CN' || location?.country_name === 'China')
    return true;

  const userIDLocation = data.userLocation?.userIDLocation;
  return (
    userIDLocation?.country_code === 'CN' ||
    userIDLocation?.country_name === 'China'
  );
};

let geoLocationRequestInFlight: Promise<boolean> | null = null;

/**
 * Fetch geolocation data.
 */
const fetchGeoLocation = async (): Promise<boolean> => {
  // If there's already a request in flight, return that promise
  if (geoLocationRequestInFlight !== null) {
    return geoLocationRequestInFlight;
  }

  // Create a new request and store it
  geoLocationRequestInFlight = (async () => {
    try {
      const response = await fetch('/data/userGeoLocation');
      const data: GeoLocationResponse = await response.json();
      const isInChina = isGeoLocationChina(data);
      setCachedGeoResult(isInChina, 'geolocation-api');
      return isInChina;
    } catch (error) {
      setCachedGeoResult(false, 'geolocation-api-failed');
      return false;
    } finally {
      // Clear the in-flight request after it completes
      geoLocationRequestInFlight = null;
    }
  })();

  return geoLocationRequestInFlight;
};

/**
 * Determines if the current user is located in China using timezone, language, browser, and geolocation checks.
 * @returns {Promise<boolean>} True if the user is detected to be in China, otherwise false.
 */
export const isUserInChina = async (): Promise<boolean> => {
  try {
    // Check timezone first (fastest)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const chinaTimezones = [
      'Asia/Shanghai',
      'Asia/Beijing',
      'Asia/Chongqing',
      'Asia/Harbin',
      'Asia/Kashgar',
      'Asia/Urumqi',
    ];
    if (chinaTimezones.includes(timezone)) {
      return true;
    }

    // Check language preference
    const language = navigator.language || (navigator as any).userLanguage;
    if (language === 'zh-CN') {
      return true;
    }

    // Check for common China-specific browsers/environments
    const userAgent = navigator.userAgent.toLowerCase();
    const chinaIndicators = [
      'micromessenger',
      'qqbrowser',
      'baidubrowser',
      'sogou',
      'maxthon',
    ];
    if (chinaIndicators.some((indicator) => userAgent.includes(indicator))) {
      return true;
    }

    // Check cached geolocation result
    const cachedGeoResult = getCachedGeoResult();
    if (cachedGeoResult !== null) {
      return cachedGeoResult;
    }

    // If no cached result, fetch geolocation data (first visit)
    const ipBasedResult = await fetchGeoLocation();
    return ipBasedResult;
  } catch (error) {
    return false;
  }
};
